import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { db } from "../../firebase";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { differenceInDays, isSameMonth, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import api from "../../services/api";

export default function CurrentClientsTable({
  clients,
  selectedMonth,
  selectedYear,
}) {
  const [receiptCounts, setReceiptCounts] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuClient, setMenuClient] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleMenuOpen = (event, clientUid) => {
    setAnchorEl(event.currentTarget);
    setMenuClient(clientUid);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuClient(null);
  };

  const handleDownloadZip = async () => {
    if (!menuClient) return;
    try {
      const lookupRes = await api.post("/accountant/lookup-receipts", {
        clientUid: menuClient,
        month: selectedMonth,
        year: selectedYear,
      });

      const receiptIds = lookupRes.data.receiptIds || [];

      if (receiptIds.length === 0) {
        throw new Error("No receipts found for this period.");
      }

      const zipRes = await api.post(
        "/receipts/zip",
        { receiptIds },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([zipRes.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `receipts-${menuClient}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setShowSuccess(true);
    } catch (err) {
      console.error("Download ZIP failed:", err);
      setErrorMsg("❌ ZIP download failed: " + err.message);
      setShowError(true);
    } finally {
      handleMenuClose();
    }
  };

  const handleDownloadPDF = async () => {
    if (!menuClient) return;
    try {
      const res = await api.post(
        `/accountant/summary-pdf`,
        {
          clientUid: menuClient,
          month: selectedMonth,
          year: selectedYear,
        },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `summary-${menuClient}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowSuccess(true);
    } catch (err) {
      console.error("❌ PDF Download Error:", err);
      setErrorMsg("PDF download failed. Please try again.");
      setShowError(true);
    }
    handleMenuClose();
  };

  useEffect(() => {
    const fetchReceiptCounts = async () => {
      const results = {};

      for (const c of clients) {
        const snap = await getDocs(
          query(collection(db, "receipts"), where("uid", "==", c.clientUid))
        );

        const docs = snap.docs;

        const latest = docs
          .map((d) => {
            const data = d.data();
            return data.uploadedAt instanceof Timestamp
              ? data.uploadedAt.toDate()
              : data.uploadedAt
              ? new Date(data.uploadedAt)
              : null;
          })
          .filter(Boolean)
          .sort((a, b) => b - a)[0];

        results[c.clientUid] = {
          count: docs.length,
          lastReceipt: latest || null,
        };
      }

      setReceiptCounts(results);
    };

    fetchReceiptCounts();
  }, [clients]);

  if (!clients.length) return null;

  const filteredClients = clients.filter((c) =>
    c.clientEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 2, overflowX: "auto", mb: 4 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Active Clients
      </Typography>
      <TextField
        variant="outlined"
        size="small"
        placeholder="Search clients..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2, width: "100%", maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Receipts</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last Upload</TableCell>
            <TableCell>Profile</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredClients.map((c) => {
            const info = receiptCounts[c.clientUid];
            const lastReceipt = info?.lastReceipt || null;

            let statusChip;
            if (!info || info.count === 0) {
              statusChip = <Chip label="🆕 New client" color="info" />;
            } else if (!lastReceipt) {
              statusChip = <Chip label="⏸️ Inactive" color="default" />;
            } else {
              const daysAgo = differenceInDays(new Date(), lastReceipt);
              if (
                lastReceipt &&
                isSameMonth(
                  new Date(selectedYear, selectedMonth, 1),
                  lastReceipt
                )
              ) {
                statusChip = <Chip label="✅ Active" color="success" />;
              } else if (daysAgo <= 30) {
                statusChip = (
                  <Chip label="⚠️ No uploads this month" color="warning" />
                );
              } else {
                statusChip = <Chip label="⏸️ Inactive" color="default" />;
              }
            }

            return (
              <TableRow key={c.id}>
                <TableCell>{c.clientEmail}</TableCell>
                <TableCell>{info?.count ?? 0}</TableCell>
                <TableCell>{statusChip}</TableCell>
                <TableCell>
                  {lastReceipt
                    ? formatDistanceToNow(lastReceipt, { addSuffix: true })
                    : "—"}
                </TableCell>
                <TableCell>
                  <Link to={`/accountant/client/${c.id}`}>View</Link>
                </TableCell>
                <TableCell>
                  <IconButton onClick={(e) => handleMenuOpen(e, c.clientUid)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDownloadPDF}>📄 Download Summary PDF</MenuItem>
        <MenuItem onClick={handleDownloadZip}>
          📦 Download All Receipts (ZIP)
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose}>Cancel</MenuItem>
      </Menu>
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          ✅ Download successful!
        </Alert>
      </Snackbar>
      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
